import { useCallback, useRef, useState } from 'react';
import {
  ContentAudience,
  WorkflowEventTypes,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';
import PQueue from 'p-queue';

import { useMplServiceClient } from '../../../contexts/MplServiceClientContext';
import { createWorkflowEventWithStatePolling } from '../../../api/CreateWorkflowEventWithStatePolling';

export type PageStep = 'input' | 'fetching' | 'confirmation' | 'executing' | 'results';

/** A content item resolved from a UUID, ready for display and selection. */
export interface ContentItem {
  readonly contentId: string;
  readonly title: string;
  readonly author: string;
  readonly contentAudience: ContentAudience;
  readonly publicationState: WorkflowState;
  readonly publicationWorkflowId: number;
}

/** A UUID that could not be resolved to a valid content workflow. */
export interface FetchError {
  readonly contentId: string;
  readonly reason: string;
}

export type ExecutionStatus = 'queued' | 'un-publishing' | 'succeeded' | 'failed';

/** The outcome of a single unpublish attempt. */
export interface ExecutionResult {
  readonly contentId: string;
  readonly title: string;
  readonly status: ExecutionStatus;
  readonly error?: string;
}

export interface BatchUnpublishPageViewModel {
  readonly step: PageStep;
  readonly rawText: string;
  readonly inputError: string | undefined;
  readonly items: readonly ContentItem[];
  readonly fetchErrors: readonly FetchError[];
  readonly selectedItems: readonly ContentItem[];
  readonly results: readonly ExecutionResult[];
  readonly progressCurrent: number;
  readonly progressTotal: number;
  readonly setRawText: (text: string) => void;
  readonly handleFileUpload: (file: File) => void;
  readonly submitInput: () => void;
  readonly setSelectedItems: (items: readonly ContentItem[]) => void;
  readonly confirmUnpublish: () => void;
  readonly retryFailed: () => void;
  readonly startOver: () => void;
  readonly goBack: () => void;
}

const UUID_REGEX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

// Batch unpublishes can wait behind queued workflow events, so allow per-item polling up to 15 minutes.
const UNPUBLISH_POLL_INTERVAL_MS = 2000;
const UNPUBLISH_MAX_POLL_ATTEMPTS = (15 * 60 * 1000) / UNPUBLISH_POLL_INTERVAL_MS;

function parseUuids(text: string): readonly string[] {
  const matches = text.match(UUID_REGEX) ?? [];
  return [...new Set(matches.map((id: string) => id.toLowerCase()))];
}

function extractErrorMessage(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const response = (e as { response?: { status?: number } }).response;
    if (response?.status === 404) return 'Content not found';
    if (response?.status === 403) return 'Access denied';
    if (response?.status) return `Server error (${response.status})`;
  }
  if (e instanceof Error) return e.message;
  return 'Unknown error';
}

export function useBatchUnpublishPageViewModel(): BatchUnpublishPageViewModel {
  const client = useMplServiceClient();

  const [step, setStep] = useState<PageStep>('input');
  const [rawText, setRawTextState] = useState('');
  const [inputError, setInputError] = useState<string | undefined>(undefined);
  const [items, setItems] = useState<readonly ContentItem[]>([]);
  const [fetchErrors, setFetchErrors] = useState<readonly FetchError[]>([]);
  const [selectedItems, setSelectedItems] = useState<readonly ContentItem[]>([]);
  const [results, setResults] = useState<readonly ExecutionResult[]>([]);
  const [progressCurrent, setProgressCurrent] = useState(0);
  const [progressTotal, setProgressTotal] = useState(0);

  // Each call to submitInput / runUnpublishBatch captures the current invocation number;
  // startOver / goBack bump it to invalidate any in-flight work from prior invocations.
  const invocationRef = useRef(0);

  // When the user edits the textarea, clear any prior error so it doesn't linger.
  const setRawText = useCallback((text: string) => {
    setRawTextState(text);
    setInputError(undefined);
  }, []);

  // When a file is uploaded, read its text content into rawText.
  const handleFileUpload = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result;
        if (typeof text === 'string') setRawText(text);
      };
      reader.readAsText(file);
    },
    [setRawText],
  );

  // When input is submitted, parse UUIDs and fetch content info for each.
  const submitInput = useCallback(async () => {
    const uuids = parseUuids(rawText);
    if (uuids.length === 0) {
      setInputError('No content UUIDs found in the input.');
      return;
    }
    setInputError(undefined);

    setStep('fetching');
    setProgressCurrent(0);
    setProgressTotal(uuids.length);
    const thisInvocation = ++invocationRef.current;

    const queue = new PQueue({ concurrency: 4 });
    const resolved: ContentItem[] = [];
    const errors: FetchError[] = [];

    const tasks = uuids.map((contentId) =>
      queue.add(async () => {
        if (invocationRef.current !== thisInvocation) return;
        try {
          const { workflow } = await client.getContentWorkflow(contentId);
          if (invocationRef.current !== thisInvocation) return;
          resolved.push({
            contentId,
            title: workflow.draftContentMetadata?.contentName ?? '(untitled)',
            author: workflow.ownerProfile?.name ?? workflow.ownerId ?? '(unknown)',
            contentAudience: workflow.contentAudience,
            // @ts-expect-error TS2322 - strict migration
            publicationState: workflow.publicationState,
            publicationWorkflowId: workflow.publicationWorkflowId,
          });
        } catch (e) {
          if (invocationRef.current !== thisInvocation) return;
          errors.push({ contentId, reason: extractErrorMessage(e) });
        }
        if (invocationRef.current !== thisInvocation) return;
        setProgressCurrent((prev) => prev + 1);
      }),
    );

    await Promise.all(tasks);

    if (invocationRef.current !== thisInvocation) return;

    setItems(resolved);
    setFetchErrors(errors);
    setSelectedItems(
      resolved.filter(
        (item) =>
          item.publicationState === WorkflowState.PUBLISHED ||
          item.publicationState === WorkflowState.FINALIZED,
      ),
    );
    setStep('confirmation');
  }, [rawText, client]);

  // When asked to unpublish a batch, run the unpublish events through a bounded queue
  // and stream per-item results back as they complete.
  const runUnpublishBatch = useCallback(async (batch: readonly ContentItem[]) => {
    setStep('executing');
    setProgressCurrent(0);
    setProgressTotal(batch.length);
    const thisInvocation = ++invocationRef.current;

    // Pre-populate all items as queued so the table renders immediately.
    const executionResults: ExecutionResult[] = batch.map((item) => ({
      contentId: item.contentId,
      title: item.title,
      status: 'queued' as ExecutionStatus,
    }));
    setResults([...executionResults]);

    const queue = new PQueue({ concurrency: 4 });

    const tasks = batch.map((item, index) =>
      queue.add(async () => {
        if (invocationRef.current !== thisInvocation) return;

        // Mark as in-progress.
        executionResults[index] = {
          contentId: item.contentId,
          title: item.title,
          status: 'un-publishing',
        };
        setResults([...executionResults]);

        try {
          await createWorkflowEventWithStatePolling(
            item.contentId,
            item.publicationWorkflowId,
            WorkflowEventTypes.UNPUBLISH,
            '',
            undefined,
            undefined,
            undefined,
            {
              maxPollAttempts: UNPUBLISH_MAX_POLL_ATTEMPTS,
              pollIntervalMs: UNPUBLISH_POLL_INTERVAL_MS,
            },
          );
          if (invocationRef.current !== thisInvocation) return;
          executionResults[index] = {
            contentId: item.contentId,
            title: item.title,
            status: 'succeeded',
          };
        } catch (e) {
          if (invocationRef.current !== thisInvocation) return;
          executionResults[index] = {
            contentId: item.contentId,
            title: item.title,
            status: 'failed',
            error: extractErrorMessage(e),
          };
        }
        if (invocationRef.current !== thisInvocation) return;
        setProgressCurrent((prev) => prev + 1);
        setResults([...executionResults]);
      }),
    );

    await Promise.all(tasks);

    if (invocationRef.current !== thisInvocation) return;
    setStep('results');
  }, []);

  // When the user confirms, fire unpublish events for all selected items.
  const confirmUnpublish = useCallback(async () => {
    if (selectedItems.length === 0) return;
    await runUnpublishBatch(selectedItems);
  }, [selectedItems, runUnpublishBatch]);

  // When retry is clicked, re-execute unpublish only for previously failed items.
  const retryFailed = useCallback(async () => {
    const failedItems = results
      .filter((r) => r.status === 'failed')
      .map((r) => items.find((i) => i.contentId === r.contentId))
      .filter((i): i is ContentItem => i !== undefined);

    if (failedItems.length === 0) return;
    await runUnpublishBatch(failedItems);
  }, [results, items, runUnpublishBatch]);

  // When start over is clicked, cancel any in-flight work and reset all state.
  const startOver = useCallback(() => {
    invocationRef.current++;
    setStep('input');
    setRawText('');
    setItems([]);
    setFetchErrors([]);
    setSelectedItems([]);
    setResults([]);
    setProgressCurrent(0);
    setProgressTotal(0);
  }, [setRawText]);

  // When go back is clicked, cancel in-flight work and return to input step.
  const goBack = useCallback(() => {
    invocationRef.current++;
    setStep('input');
  }, []);

  return {
    step,
    rawText,
    inputError,
    items,
    fetchErrors,
    selectedItems,
    results,
    progressCurrent,
    progressTotal,
    setRawText,
    handleFileUpload,
    submitInput,
    setSelectedItems,
    confirmUnpublish,
    retryFailed,
    startOver,
    goBack,
  };
}
